import { ChevronLeft, ChevronRight, FileDown, GraduationCap, Plus, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { AVATARES } from '@/lib/avatares';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga, TabsModulo } from '../curso/componentes';

import { EtapaEnarmDialog, EventoDialog } from './EventoDialog';
import {
  abrirCronograma,
  COLORES_EVENTO,
  DIAS,
  fechaDia,
  MESES,
  rango,
  useEventos,
  usePlan,
  type EventoEnarm,
  type EventoPersonal,
  type EventosCalendario,
  type StudyPlan,
} from './queries';

type Dia = StudyPlan['rounds'][number]['days'][number] & { round: number };
/** Marca de una etapa ENARM en un día: de un solo día, o inicio / cierre de un periodo. */
type Marca = { etapa: EventoEnarm; tipo: 'dia' | 'inicio' | 'fin' };
/** Diálogo abierto: nuevo evento en una fecha, edición de uno personal o detalle de una etapa. */
type Dialogo =
  | { tipo: 'nuevo'; fecha: string }
  | { tipo: 'editar'; evento: EventoPersonal }
  | { tipo: 'etapa'; etapa: EventoEnarm }
  | null;

const SIN_EVENTOS: EventosCalendario = { personal: [], enarm: [] };
/** Un evento personal muy largo no llena el calendario: se pinta como mucho un año. */
const MAX_DIAS_RANGO = 366;

function sumarDia(d: string): string {
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, dd + 1)).toISOString().slice(0, 10);
}

function agregar<T>(m: Map<string, T[]>, k: string, v: T) {
  const l = m.get(k);
  if (l) l.push(v);
  else m.set(k, [v]);
}

const COLOR: Record<string, string> = {
  gineco: 'border-l-[#F37EA8]',
  cirugia: 'border-l-[#0FD0CD]',
  pedia: 'border-l-[#7E9DAF]',
  medint: 'border-l-[#5B7BB8]',
};

/** Mi plan de estudio (port de `temario/consultar`): calendario mensual y agenda en móvil. */
export function PlanPage() {
  return (
    <PermissionGate permiso="study_plan.access" modulo="Mi plan de estudio">
      <Plan />
    </PermissionGate>
  );
}

function hoy() {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function Plan() {
  const { data, error, isPending } = usePlan();
  // Si los eventos fallan, el plan se muestra igual (sin eventos).
  const eventos = useEventos();
  const [pdf, setPdf] = useState(false);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;
  const calendario = (
    <Calendario
      plan={data}
      eventos={eventos.data ?? SIN_EVENTOS}
      onNuevo={(fecha) => setDialogo({ tipo: 'nuevo', fecha })}
      onEditar={(evento) => setDialogo({ tipo: 'editar', evento })}
      onEtapa={(etapa) => setDialogo({ tipo: 'etapa', etapa })}
    />
  );
  return (
    <div className="space-y-6">
      <TabsModulo />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="titulo-seccion">
          <span className="subrayado-verde">Mi plan de estudio</span>
        </h1>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-cs btn-contorno"
            onClick={() => setDialogo({ tipo: 'nuevo', fecha: hoy() })}
          >
            <Plus size={18} /> Agregar evento
          </button>
          {data && (
            <Link
              to="/curso-enarm/temario/organizar?reconfigurar=1"
              className="btn-cs btn-contorno"
            >
              <Settings2 size={18} /> Configurar
            </Link>
          )}
          {data && (
            <button
              type="button"
              className="btn-cs btn-primario"
              disabled={pdf}
              onClick={() => {
                setPdf(true);
                abrirCronograma()
                  .catch((e: unknown) => toast.error(mensajeDe(e)))
                  .finally(() => setPdf(false));
              }}
            >
              <FileDown size={18} /> {pdf ? 'Generando…' : 'Descargar PDF'}
            </button>
          )}
        </div>
      </div>
      {!data ? (
        <>
          <section className="panel-vidrio relative mx-auto mt-16 max-w-xl text-center">
            <img
              src={AVATARES.doctoraSenala}
              alt=""
              className="pointer-events-none mx-auto -mt-24 mb-2 h-28 w-auto sm:-mt-28 sm:h-32"
            />
            <p className="font-bold">Aún no tienes un plan de estudio.</p>
            <p className="mt-2 text-sm text-texto-2">
              Elige cuántas rondas quieres y qué días estudiarás; nosotros repartimos el temario
              hasta el ENARM.
            </p>
            <Link to="/curso-enarm/temario/organizar" className="btn-cs btn-exito mt-5">
              Organizar mi estudio
            </Link>
          </section>
          {calendario}
        </>
      ) : (
        // Como en `temario/consultar`: la doctora y el doctor flanquean el calendario (escritorio).
        <div className="grid gap-6 2xl:grid-cols-[7rem_minmax(0,1fr)_7rem]">
          <img
            src={AVATARES.doctoraIzq}
            alt=""
            className="pointer-events-none hidden w-full self-center 2xl:block"
          />
          {calendario}
          <div className="hidden flex-col items-center justify-center gap-6 2xl:flex">
            <img src={AVATARES.doctorSenala} alt="" className="pointer-events-none w-full" />
            <img
              src="/img/isotipo-cerebro-azul.png"
              alt=""
              className="solo-claro h-14 w-auto opacity-80"
            />
            <img
              src="/img/isotipo-cerebro-blanco.png"
              alt=""
              className="solo-oscuro h-14 w-auto opacity-80"
            />
          </div>
        </div>
      )}
      {dialogo?.tipo === 'nuevo' && (
        <EventoDialog evento={null} fecha={dialogo.fecha} onClose={() => setDialogo(null)} />
      )}
      {dialogo?.tipo === 'editar' && (
        <EventoDialog
          evento={dialogo.evento}
          fecha={dialogo.evento.date}
          onClose={() => setDialogo(null)}
        />
      )}
      {dialogo?.tipo === 'etapa' && (
        <EtapaEnarmDialog etapa={dialogo.etapa} onClose={() => setDialogo(null)} />
      )}
    </div>
  );
}

function Calendario({
  plan,
  eventos,
  onNuevo,
  onEditar,
  onEtapa,
}: {
  plan: StudyPlan | null;
  eventos: EventosCalendario;
  onNuevo: (fecha: string) => void;
  onEditar: (evento: EventoPersonal) => void;
  onEtapa: (etapa: EventoEnarm) => void;
}) {
  const dias = useMemo(() => {
    const m = new Map<string, Dia>();
    for (const r of plan?.rounds ?? [])
      for (const d of r.days) m.set(d.date, { ...d, round: r.round });
    return m;
  }, [plan]);
  // Los personales se pintan en cada día de su rango; las etapas ENARM solo en su inicio y cierre
  // (los periodos largos, como la revisión documental, llenarían meses enteros).
  const personales = useMemo(() => {
    const m = new Map<string, EventoPersonal[]>();
    for (const e of eventos.personal) {
      let d = e.date;
      for (let n = 0; n < MAX_DIAS_RANGO; n++) {
        agregar(m, d, e);
        if (!e.endDate || d >= e.endDate) break;
        d = sumarDia(d);
      }
    }
    return m;
  }, [eventos.personal]);
  const marcas = useMemo(() => {
    const m = new Map<string, Marca[]>();
    for (const etapa of eventos.enarm) {
      if (!etapa.endDate) agregar(m, etapa.date, { etapa, tipo: 'dia' });
      else {
        agregar(m, etapa.date, { etapa, tipo: 'inicio' });
        agregar(m, etapa.endDate, { etapa, tipo: 'fin' });
      }
    }
    return m;
  }, [eventos.enarm]);

  const hoyStr = hoy();
  const primero = plan?.rounds[0]?.start ?? hoyStr;
  const inicial = hoyStr >= primero ? hoyStr : primero;
  const [mes, setMes] = useState(() => inicial.slice(0, 7));
  const [y, m] = mes.split('-').map(Number) as [number, number];
  const mover = (delta: number) => {
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMes(`${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  };
  const offset = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7; // lunes primero
  const enMes = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const celdas = Array.from({ length: Math.ceil((offset + enMes) / 7) * 7 }, (_, i) => {
    const n = i - offset + 1;
    return n >= 1 && n <= enMes ? `${mes}-${String(n).padStart(2, '0')}` : null;
  });
  const fechasDelMes = [...new Set([...dias.keys(), ...personales.keys(), ...marcas.keys()])]
    .filter((d) => d.startsWith(mes))
    .sort();
  // Etapas ENARM que tocan el mes (incluye los periodos que lo cruzan sin empezar ni terminar en él).
  const finDeMes = `${mes}-${String(enMes).padStart(2, '0')}`;
  const etapasDelMes = eventos.enarm.filter(
    (e) => e.date <= finDeMes && (e.endDate ?? e.date) >= `${mes}-01`,
  );

  return (
    <section className="panel-vidrio space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-cs btn-vidrio"
            aria-label="Mes anterior"
            onClick={() => mover(-1)}
          >
            <ChevronLeft size={18} />
          </button>
          <h2 className="min-w-44 text-center text-lg font-extrabold capitalize">
            {MESES[m - 1]} {y}
          </h2>
          <button
            type="button"
            className="btn-cs btn-vidrio"
            aria-label="Mes siguiente"
            onClick={() => mover(1)}
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <button
          type="button"
          className="btn-cs btn-contorno"
          onClick={() => setMes(hoyStr.slice(0, 7))}
        >
          Hoy
        </button>
      </div>
      {plan && (
        <p className="text-xs text-texto-3">
          {plan.rounds
            .map(
              (r) =>
                `Ronda ${String(r.round)}: ${fechaDia(r.start, false)} – ${fechaDia(r.end, false)}`,
            )
            .join(' · ')}
        </p>
      )}

      {/* Escritorio: cuadrícula del mes */}
      <div className="hidden md:block">
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-extrabold text-texto-3 uppercase">
          {[1, 2, 3, 4, 5, 6, 0].map((d) => (
            <span key={d}>{(DIAS[d] ?? '').slice(0, 3)}</span>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {celdas.map((c, i) => {
            const d = c ? dias.get(c) : undefined;
            return (
              <div
                key={i}
                className={cn(
                  'group min-h-28 rounded-lg border border-white/10 p-1.5',
                  !c && 'border-transparent',
                  c === hoyStr && 'ring-2 ring-cian-500',
                )}
              >
                {c && (
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      aria-label={`Agregar evento el ${fechaDia(c)}`}
                      title="Agregar evento"
                      className="rounded p-0.5 text-texto-3 opacity-0 group-hover:opacity-100 hover:bg-white/10 hover:text-texto focus-visible:opacity-100"
                      onClick={() => onNuevo(c)}
                    >
                      <Plus size={14} />
                    </button>
                    <p className="text-xs font-bold text-texto-3 tabular">{Number(c.slice(8))}</p>
                  </div>
                )}
                <ul className="mt-1 space-y-1">
                  {c && (
                    <EventosDelDia
                      marcas={marcas.get(c)}
                      personales={personales.get(c)}
                      onEditar={onEditar}
                      onEtapa={onEtapa}
                    />
                  )}
                  {d?.items.map((t, k) => (
                    <li key={k}>
                      <Link
                        to={`/curso-enarm/clases/${String(t.classId)}`}
                        title={`${t.module} (${t.specialty})`}
                        className={cn(
                          'block truncate rounded border-l-4 bg-white/5 px-1.5 py-0.5 text-[11px] hover:bg-white/10',
                          COLOR[t.area],
                        )}
                      >
                        {t.module}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Móvil: agenda del mes */}
      <ul className="space-y-3 md:hidden">
        {fechasDelMes.map((f) => (
          <li key={f} className={cn('card-vidrio p-3', f === hoyStr && 'ring-2 ring-cian-500')}>
            <p className="text-sm font-extrabold capitalize">{fechaDia(f)}</p>
            <ul className="mt-2 space-y-1">
              <EventosDelDia
                grande
                marcas={marcas.get(f)}
                personales={personales.get(f)}
                onEditar={onEditar}
                onEtapa={onEtapa}
              />
              {dias.get(f)?.items.map((t, k) => (
                <li key={k}>
                  <Link
                    to={`/curso-enarm/clases/${String(t.classId)}`}
                    className={cn(
                      'block rounded border-l-4 bg-white/5 px-2 py-1 text-sm',
                      COLOR[t.area],
                    )}
                  >
                    {t.module} <span className="text-xs text-texto-3">({t.specialty})</span>
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
        {fechasDelMes.length === 0 && (
          <li className="text-center text-sm text-texto-2">No tienes temas ni eventos este mes.</li>
        )}
      </ul>

      {etapasDelMes.length > 0 && (
        <div className="rounded-xl border border-verde-500/40 bg-verde-500/5 p-3">
          <p className="flex items-center gap-1.5 text-sm font-extrabold text-verde-texto">
            <GraduationCap size={16} /> Fechas oficiales del ENARM este mes
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {etapasDelMes.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => onEtapa(e)}
                >
                  <span className="font-bold">{e.title}</span>
                  <span className="text-texto-2"> · {rango(e.date, e.endDate)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-texto-3" aria-label="Simbología">
        <li className="flex items-center gap-1.5">
          <GraduationCap size={14} className="text-verde-texto" /> Fecha oficial ENARM
        </li>
        <li className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-[#720294]" /> Mis eventos
        </li>
        {plan && <li>Temas del plan: borde por área</li>}
      </ul>
    </section>
  );
}

const ETIQUETA_MARCA: Record<Marca['tipo'], string> = {
  dia: '',
  inicio: 'Inicia: ',
  fin: 'Cierra: ',
};

/** Etapas ENARM y eventos personales de un día (antes de los temas del plan). */
function EventosDelDia({
  marcas,
  personales,
  onEditar,
  onEtapa,
  grande = false,
}: {
  marcas: Marca[] | undefined;
  personales: EventoPersonal[] | undefined;
  onEditar: (evento: EventoPersonal) => void;
  onEtapa: (etapa: EventoEnarm) => void;
  grande?: boolean;
}) {
  const texto = grande ? 'text-sm px-2 py-1' : 'text-[11px] px-1.5 py-0.5';
  return (
    <>
      {marcas?.map(({ etapa, tipo }) => (
        <li key={`e${String(etapa.id)}${tipo}`}>
          <button
            type="button"
            title={`${ETIQUETA_MARCA[tipo]}${etapa.title}`}
            onClick={() => onEtapa(etapa)}
            className={cn(
              'flex w-full items-center gap-1 truncate rounded bg-verde-500/20 text-left font-bold text-verde-texto hover:bg-verde-500/30',
              texto,
            )}
          >
            <GraduationCap size={grande ? 14 : 12} className="shrink-0" />
            <span className="truncate">
              {ETIQUETA_MARCA[tipo]}
              {etapa.title}
            </span>
          </button>
        </li>
      ))}
      {personales?.map((e) => (
        <li key={`p${String(e.id)}`}>
          <button
            type="button"
            title={e.notes ? `${e.title}\n${e.notes}` : e.title}
            onClick={() => onEditar(e)}
            className={cn(
              'block w-full truncate rounded text-left font-bold text-white hover:brightness-110',
              texto,
            )}
            style={{ backgroundColor: COLORES_EVENTO[e.color].hex }}
          >
            {e.time && <span className="tabular opacity-80">{e.time} </span>}
            {e.title}
          </button>
        </li>
      ))}
    </>
  );
}
